import Applicant from '@/models/onboarding/applicant.model';
import { compact } from '@/utilities/helpers';

const DEFAULT_PHONE_NUMBER_TYPE = 'HOME';

export default class ApplicantSerializer {
  static serialize(model) {
    // console.log('Applicant#serialize', arguments);
    const applicant = {};

    // market data
    // this is not sent to the backend, only serialized in the session
    applicant.nonProfessional = model.nonProfessional;

    // contact
    applicant.contact = {};
    applicant.contact.phoneNumbers = [{ phoneNumber: model.phoneNumber, phoneNumberType: DEFAULT_PHONE_NUMBER_TYPE }];
    applicant.contact.emailAddresses = [model.email];
    applicant.contact.homeAddress = {
      streetAddress: compact([model.homeAddress?.streetAddress, model.homeAddress?.streetAddress2]),
      city: model.homeAddress?.city,
      country: model.residencyCountry,
      postalCode: model.homeAddress?.postalCode,
    };
    if (!model.isForeign) {
      applicant.contact.homeAddress.state = model.homeAddress?.state;
    }

    if (model.hasDifferentMailing) {
      applicant.contact.mailingAddress = {
        streetAddress: compact([model.mailingAddress?.streetAddress, model.mailingAddress?.streetAddress2]),
        city: model.mailingAddress?.city,
        country: model.residencyCountry,
        postalCode: model.mailingAddress?.postalCode,
      };
      if (!model.isForeign) {
        applicant.contact.mailingAddress.state = model.mailingAddress?.state;
      }
    }

    // identity
    applicant.identity = {};
    applicant.identity.name = {};
    if (model.prefix) applicant.identity.name.prefix = model.prefix;
    applicant.identity.name.givenName = model.firstName;
    applicant.identity.name.familyName = model.lastName;
    if (model.suffix) applicant.identity.name.suffix = model.suffix;
    applicant.identity.dateOfBirth = model.dateOfBirth;
    applicant.identity.citizenshipCountry = model.citizenshipCountry;
    applicant.identity.socialSecurityNumber = model.socialSecurityNumber;

    applicant.cache = {
      residentCountry: model.cache?.residentCountry,
      isSecondaryApplicant: model.cache?.isSecondaryApplicant,
      hasTrustedContact: model.cache?.hasTrustedContact,
    };

    if (!model.isForeign) {
      applicant.identity.permanentResident = model.permanentResident;
    }

    // employment
    applicant.employment = {};
    applicant.employment.employer = model.employer;
    applicant.employment.employmentStatus = model.employmentStatus;
    applicant.employment.positionEmployed = model.positionEmployed;

    // disclosures
    applicant.disclosures = {};
    applicant.disclosures.isAffiliatedExchangeOrFINRA = model.isAffiliatedExchangeOrFINRA;
    if (model.isAffiliatedExchangeOrFINRA) {
      applicant.disclosures.firmName = model.firmName;
    }

    applicant.disclosures.isControlPerson = model.isControlPerson;
    if (model.isControlPerson) {
      applicant.disclosures.companySymbols = model.companySymbols
        ?.trim()
        .split(',')
        .map((val) => val.trim())
        .filter((val) => val);
    }

    applicant.disclosures.isPoliticallyExposed = model.isPoliticallyExposed;
    if (model.isPoliticallyExposed) {
      applicant.disclosures.politicalExposureDetail = {};
      applicant.disclosures.politicalExposureDetail.politicalOrganization = model.politicalOrganization;
      applicant.disclosures.politicalExposureDetail.immediateFamily = model.immediateFamily
        ?.trim()
        .split(',')
        .map((val) => val.trim())
        .filter((val) => val);
    }

    // foreign
    if (model.isForeign) {
      applicant.identity.birthCountry = model.citizenshipCountry;
      applicant.identity.foreignTaxIdCountry = applicant.cache.residentCountry || model.citizenshipCountry;
      applicant.identity.foreignTaxIdNumber = model.socialSecurityNumber;
      applicant.w_8ben = {};
      applicant.w_8ben.foreignTaxIdentificationNumber = model.socialSecurityNumber;
      applicant.w_8ben.treatyCountry = applicant.cache.residentCountry || model.citizenshipCountry;

      applicant.governmentId = {};
      applicant.governmentId.countryOfIssue = model.foreignCountryOfIssue; // BRA,
      applicant.governmentId.identificationNumber = model.foreignIdentificationNumber; // FY006554,
      applicant.governmentId.identificationType = model.foreignIdentificationType; // PASSPORT,
      applicant.governmentId.identificationIssueDate = model.foreignIdentificationIssueDate; // 2020-12-12,
      applicant.governmentId.identificationExpirationDate = model.foreignIdentificationExpirationDate; // 2030-12-12
    }

    return applicant;
  }

  static deserialize(data) {
    // console.log('Applicant#deserialize', data);
    if (Array.isArray(data)) {
      return ApplicantSerializer.deserializeArray(data);
    }

    return ApplicantSerializer.deserializeObject(data);
  }

  static deserializeArray(jsonArray) {
    // console.log('Applicant#deserializeArray', arguments);
    return jsonArray.map((el) => ApplicantSerializer.deserializeObject(el));
  }

  static deserializeObject(json, applicationId) {
    // console.log('Applicant#deserializeObject', arguments);
    const applicant = {};

    applicant.application_id = applicationId;
    applicant.email = json.contact?.emailAddresses[0] || '';
    applicant.nonProfessional = json.nonProfessional;

    applicant.homeAddress = {
      streetAddress: json.contact.homeAddress?.streetAddress[0] || '',
      streetAddress2: json.contact.homeAddress?.streetAddress[1] || '',
      city: json.contact.homeAddress?.city,
      state: json.contact.homeAddress?.state,
      postalCode: json.contact.homeAddress?.postalCode,
      country: json.contact.homeAddress?.country,
    };

    if (json.contact.mailingAddress) applicant.hasDifferentMailing = true;
    applicant.mailingAddress = {
      streetAddress: json.contact.mailingAddress?.streetAddress[0] || '',
      streetAddress2: json.contact.mailingAddress?.streetAddress[1] || '',
      city: json.contact.mailingAddress?.city,
      state: json.contact.mailingAddress?.state,
      postalCode: json.contact.mailingAddress?.postalCode,
      country: json.contact.mailingAddress?.country,
    };

    applicant.prefix = json.identity?.name?.prefix;
    applicant.firstName = json.identity?.name?.givenName;
    applicant.lastName = json.identity?.name?.familyName;
    applicant.suffix = json.identity?.name?.suffix;
    applicant.dateOfBirth = json.identity?.dateOfBirth;
    applicant.citizenshipCountry = json.identity?.citizenshipCountry;
    applicant.permanentResident = json.identity?.permanentResident || false;
    applicant.socialSecurityNumber = json.identity?.socialSecurityNumber;
    applicant.foreignTaxIdCountry = json.identity?.foreignTaxIdCountry;

    applicant.employer = json.employment?.employer;
    applicant.employmentStatus = json.employment?.employmentStatus;
    applicant.positionEmployed = json.employment?.positionEmployed;

    applicant.isAffiliatedExchangeOrFINRA = json.disclosures?.isAffiliatedExchangeOrFINRA;
    applicant.firmName = json.disclosures?.firmName;

    applicant.isControlPerson = json.disclosures?.isControlPerson;
    applicant.companySymbols = json.disclosures?.companySymbols?.join(',');

    applicant.isPoliticallyExposed = json.disclosures?.isPoliticallyExposed;
    applicant.politicalOrganization = json.disclosures?.politicalExposureDetail?.politicalOrganization;
    applicant.immediateFamily = json.disclosures?.politicalExposureDetail?.immediateFamily?.join(',');

    applicant.cache = {};
    if (json.cache) {
      applicant.cache.residentCountry = json.cache.residentCountry;
      applicant.cache.isSecondaryApplicant = json.cache.isSecondaryApplicant;
      applicant.cache.hasTrustedContact = json.cache.hasTrustedContact;
    }

    if (Array.isArray(json.contact?.phoneNumbers)) {
      applicant.phoneNumber = json.contact?.phoneNumbers[0].phoneNumber;
    }

    // foreign
    if (applicant.w_8ben) {
      applicant.socialSecurityNumber = applicant.w_8ben?.foreignTaxIdentificationNumber;
    }

    if (json.governmentId) {
      applicant.foreignCountryOfIssue = json.governmentId?.countryOfIssue;
      applicant.foreignIdentificationNumber = json.governmentId?.identificationNumber;
      applicant.foreignIdentificationType = json.governmentId?.identificationType;
      applicant.foreignIdentificationIssueDate = json.governmentId?.identificationIssueDate;
      applicant.foreignIdentificationExpirationDate = json.governmentId?.identificationExpirationDate;
    }

    return new Applicant(applicant);
  }
}
